// The patient's standing room page for a per-minute consult — the destination
// `ConsultJoinButton` navigates to (BUG #3 fix) whenever a patient re-enters a consult
// from outside the checkout redirect (`public/ClientPortal.jsx`'s "My account" list, the
// confirmation email link, a bookmark). `/merci/consultation` (`CheckoutSuccess.jsx`)
// covers the ONE-TIME landing right after Stripe Checkout, where the appointment/token
// pair comes from `lib/pendingConsult.js`'s stash; this route covers every OTHER visit,
// where the pair travels as plain query params instead — the same identity, a different
// origin.
//
// Route: `/consultation/salle?appointmentId=<id>&token=<manage_token>` (registered in
// routes/platformRoutes.jsx and tenant/TenantSite.jsx).
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { useTenantHomePath } from "@/lib/TenantContext";
import ConsultRoomFrame from "./ConsultRoomFrame";
// Relative, not "@/…": real modules under Jest, no alias there.
import { isConsultOver } from "../gateway/roomPhase";
import ReviewInviteCta from "../reviews/components/ReviewInviteCta";

export default function ConsultRoom() {
  const [sp] = useSearchParams();
  const appointmentId = sp.get("appointmentId") || sp.get("apt") || "";
  const token = sp.get("token") || "";
  // BUG-055 — the patient's way back out of the room. Resolved HERE, at the mount, because
  // the three-way rule needs the tenant document (`useTenantHomePath`) and the frame is
  // also mounted on the practitioner's side, where the way back is a different place.
  const homePath = useTenantHomePath();

  // THE EXIT SCREEN'S REVIEW INVITATION, decided at the mount for the same reason the way
  // home is: the frame is also the practitioner's, and the review is not theirs to leave.
  //
  // `onStatus` is the frame's documented seam — the poll result, handed over whenever it
  // changes — and `isConsultOver` is the ONE definition of "this consultation is over"
  // (`gateway/roomPhase.js`), the same one the frame gates its own ended block on. So the
  // invitation cannot appear a moment before the exit screen it belongs to.
  //
  // MOUNTED ONCE, then kept: `setStatus` is stable and the element sits in a fixed position
  // in the tree, so the per-poll re-render reconciles it rather than remounting it — the
  // `by-booking` read fires exactly once, not every two seconds.
  const [status, setStatus] = useState(null);

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-20 max-w-2xl mx-auto px-6" data-testid="consult-room-page">
        <ConsultRoomFrame appointmentId={appointmentId} token={token} role="patient"
                          homePath={homePath}
                          onStatus={setStatus}
                          endedSlot={isConsultOver(status) ? <ReviewInviteCta manageToken={token} /> : null} />
      </section>
    </ThemedPageLayout>
  );
}
